import React, { memo, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';

import { AccountCircle, Assignment, Mail, VpnKey, Security, Logout } from '@mui/icons-material';

import { LocalizedLink } from '../../_shared/LocalizedLink';
import { useAuth } from '../../authentication/AuthenticationContext';

export function Menu(): JSX.Element {
  const auth = useAuth();
  const location = useLocation();
  const { t } = useTranslation();

  const [currentActive, setCurrentActive] = useState<string>(location.pathname);

  const logout = (): void => {
    auth.logout();
  };

  const data = [
    {
      path: 'my-account',
      translation: 'button.my-account',
      Icon: memo(() => <AccountCircle className='menu-icon' />),
    },
    {
      path: 'personal-information',
      translation: 'account.settings.nav.details',
      Icon: memo(() => <Assignment className='menu-icon' />),
    },
    {
      path: 'communication-preferences',
      translation: 'account.settings.nav.communication',
      Icon: memo(() => <Mail className='menu-icon' />),
    },
    {
      path: 'change-password',
      translation: 'account.settings.nav.password',
      Icon: memo(() => <VpnKey className='menu-icon' />),
      requires: auth.user.requiresPassword,
    },
    {
      path: 'mfa',
      translation: 'account.settings.nav.mfa',
      Icon: memo(() => <Security className='menu-icon' />),
    },
  ];

  useEffect(() => {
    setCurrentActive(location.pathname);
  }, [location.pathname]);

  return (
    <div className='account-menu'>
      <div className='text-center menu-header'>
        <h3 data-cy='welcome-title-text'>{t('auth.register.sign-up')}</h3>
        <h3 className='username' data-cy='desktop-nickname-text'>{auth.user.nickName}</h3>
        <button type='button' data-cy='desktop-logout-button' className='btn btn-secondary' onClick={logout}>
          <Logout className='icon' />
          {t('button.logout')}
        </button>
      </div>

      <div className='menu-links'>
        {data.map((menuOption) => {
          const fullPath = `/account/${menuOption.path}`;
          if (menuOption.requires == null || menuOption.requires) {
            return (
              <LocalizedLink
                key={menuOption.path}
                isNavLink
                className='menu-link'
                to={fullPath}
                data-cy={`${menuOption.path}-link`}
                onMouseEnter={() => setCurrentActive(fullPath)}
                onMouseLeave={() => setCurrentActive(location.pathname)}
              >
                {currentActive.includes(fullPath) && (
                  <motion.span
                    layoutId='menu-bar'
                    className='menu-bar'
                    transition={{
                      type: 'spring',
                      duration: 0.5,
                    }}
                  />
                )}
                <menuOption.Icon />
                {t(menuOption.translation)}
              </LocalizedLink>
            );
          }
          return null;
        })}
      </div>
    </div>
  );
}
